import React, { useMemo, useState } from 'react';
import { Stack, Title, Group, Select, Textarea, Text, Button, Loader, Alert } from '@mantine/core';
import IPhonePreview from './IPhonePreview';
import { useMediaQuery } from '@mantine/hooks';
import { useCampaign } from '../../context/CampaignContext';

const MAX_CHARACTERS = 200;

function MessageCreationStep({ onBack, onNext }) {
  const { 
    selectedTemplate, 
    setSelectedTemplate, 
    message, 
    setMessage,
    templateVariables,
    messageTemplates,
    complianceMessages,
    loadingState,
    apiErrors,
    complianceStatus,
    complianceErrors,
    complianceWarnings
  } = useCampaign();
  
  const [messageError, setMessageError] = useState('');
  const [messageTouched, setMessageTouched] = useState(false);
  
  const isMobile = useMediaQuery('(max-width: 768px)');
  
  // Calculate adjusted character count considering template variable lengths
  const calculateAdjustedCharCount = (text, variables) => {
    if (!variables || !variables.length || !text) return text ? text.length : 0;
    
    let adjustedCount = text.length;
    
    // Find all template variables in the message
    const matches = text.match(/\{([A-Z_]+)\}/g) || [];
    
    for (const match of matches) {
      const varName = match.substring(1, match.length - 1); // Remove { }
      const variable = variables.find(v => v.name === varName);
      
      if (variable) {
        // Subtract the full variable length (including braces) and add the estimated length
        adjustedCount -= match.length;
        adjustedCount += parseInt(variable.est_length, 10) || 0;
      }
    }
    
    return adjustedCount;
  };

  // Get a list of missing required variables
  const getMissingRequiredVariables = (text, variables) => {
    if (!variables || !variables.length || !text) return [];
    const requiredVars = variables.filter(v => v.required);
    return requiredVars.filter(v => !text.includes(`{${v.name}}`));
  };
  
  const charCount = calculateAdjustedCharCount(message, templateVariables);
  const missingRequiredVars = getMissingRequiredVariables(message, templateVariables);
  
  // Get a list of all required variables for the description
  const requiredVarsList = useMemo(() => {
    if (!templateVariables || !templateVariables.length) return '';
    const requiredVars = templateVariables.filter(v => v.required);
    return requiredVars.map(v => `{${v.name}}`).join(', ');
  }, [templateVariables]);
  
  // Create variables object from the examples for preview
  const previewVariables = useMemo(() => {
    if (!templateVariables || templateVariables.length === 0) {
      // Default fallback values if template variables aren't loaded yet
      return {
        SALON_NAME: 'Your Salon',
        CLIENT_NAME: 'Client',
        DAYS_SINCE_LAST_BOOKED: '30',
        WEEKS_SINCE_LAST_BOOKED: '4',
        MONTHS_SINCE_LAST_BOOKED: '1',
        REBOOK_LINK: 'booking-link',
      };
    }
    
    return templateVariables.reduce((acc, v) => {
      acc[v.name] = v.example || `{${v.name}}`;
      return acc;
    }, {});
  }, [templateVariables]);

  const handleTemplateSelect = (val) => {
    setSelectedTemplate(val);
    const template = messageTemplates.find(t => t.name === val);
    if (template) {
      setMessage(template.message);
      // Reset error state when template is selected
      setMessageError('');
      setMessageTouched(false);
    }
  };
  
  const validateMessage = () => {
    setMessageTouched(true);
    
    if (missingRequiredVars.length > 0) {
      setMessageError(`Message must include: ${missingRequiredVars.map(v => `{${v.name}}`).join(', ')}`);
      return false;
    }
    
    if (charCount > MAX_CHARACTERS) {
      setMessageError(`Message must be ${MAX_CHARACTERS} characters or less.`);
      return false;
    }
    
    setMessageError('');
    return true;
  };
  
  const handleMessageChange = (e) => {
    setMessage(e.target.value);
    
    // If the message has been touched (blurred before), validate on each change
    if (messageTouched) {
      validateMessage();
    }
  };
  
  const handleMessageBlur = () => {
    validateMessage();
  };
  
  const handleNext = () => {
    if (validateMessage()) {
      onNext();
    }
  };

  // Create template options for the Select component
  const templateOptions = useMemo(() => {
    return messageTemplates.map(t => ({ value: t.name, label: t.name }));
  }, [messageTemplates]); 

  return (
    <Stack gap="md">
      <Title order={3}>Message Creation</Title>
      <Group gap="md" align="flex-start" wrap="wrap">
        <Stack gap={4} style={{ flex: 1, minWidth: 320 }}>
          <Select
            label="Choose a Template"
            data={templateOptions}
            value={selectedTemplate}
            onChange={handleTemplateSelect}
            placeholder="Select a template or write your own"
            clearable
            loading={loadingState.templates}
            error={apiErrors.templates}
          />
          <Textarea
            label="Message"
            value={message}
            onChange={handleMessageChange}
            onBlur={handleMessageBlur}
            minRows={3}
            maxRows={5}
            error={messageError}
            required
            description={loadingState.templateVars 
              ? "Loading..." 
              : requiredVarsList.length > 0 
                ? `Max ${MAX_CHARACTERS} characters. Message must include ${requiredVarsList}.`
                : `Max ${MAX_CHARACTERS} characters.`
            }
          />
          <Group gap={8} align="center">
            <Text size="sm" color={charCount > MAX_CHARACTERS ? 'red' : 'dimmed'}>{charCount}/{MAX_CHARACTERS}</Text>
          </Group>
          <Stack gap={2} mt={8}>
            <Text size="xs" weight={700}>Available Variables:</Text>
            {loadingState.templateVars ? (
              <Group>
                <Loader size="xs" />
                <Text size="xs" color="dimmed">Loading variables...</Text>
              </Group>
            ) : apiErrors.templateVars ? (
              <Text size="xs" color="red">Error loading variables: {apiErrors.templateVars}</Text>
            ) : (
              templateVariables && templateVariables.map(v => (
                <Text key={v.name} size="xs" color="dimmed">
                  {'{'}{v.name}{'}'} - {v.description} 
                </Text>
              ))
            )}
          </Stack>
          <Stack gap={2} mt={8}>
            <Text size="xs" color="yellow" weight={700}>Compliance:</Text>
            {loadingState.complianceMessages ? (
              <Group>
                <Loader size="xs" />
                <Text size="xs" color="dimmed">Loading compliance guidelines...</Text>
              </Group>
            ) : apiErrors.complianceMessages ? (
              <Text size="xs" color="red">Error loading compliance guidelines</Text>
            ) : complianceMessages && complianceMessages.length > 0 ? (
              complianceMessages.map((message, index) => (
                <Text key={index} size="xs" color="yellow">• {message}</Text>
              ))
            ) : (
              <Text size="xs" color="yellow">• No compliance messages available</Text>
            )}
          </Stack>
          {complianceStatus === 'fail' && (
            <Stack gap="xs" mt={8}>
              {complianceErrors.length > 0 && (
                <Alert color="red" title="Compliance Errors">
                  {complianceErrors.map((error, index) => (
                    <Text key={index} size="sm" color="red">• {error}</Text>
                  ))}
                </Alert>
              )}
              {complianceWarnings.length > 0 && (
                <Alert color="yellow" title="Compliance Warnings">
                  {complianceWarnings.map((warning, index) => (
                    <Text key={index} size="sm" color="yellow">• {warning}</Text>
                  ))}
                </Alert>
              )}
            </Stack>
          )}
        </Stack>
        {!isMobile && 
          <Stack gap={4} align="center" mt={-32}>
            <IPhonePreview message={message} variables={previewVariables} />
          </Stack>
        }
      </Group>
      {isMobile && 
        <Stack gap={4} align="center">
          <IPhonePreview message={message} variables={previewVariables} />
        </Stack>
      }
      <Group justify="space-between" mt="md">
        <Button variant="default" onClick={onBack}>&larr; Back</Button>
        <Button 
          onClick={handleNext} 
          disabled={loadingState.templateVars || missingRequiredVars.length > 0 || charCount > MAX_CHARACTERS || !message}
        >
          Next
        </Button>
      </Group>
    </Stack>
  );
}

export default MessageCreationStep; 